<template>
  <div
    v-if="currentUser"
    class="py-2"
  >

    <BSidebar
      id="sbASMCMemoProcessingFilters"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>

      <BContainer>
        <BRow>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Month"
              label-for="filterMonth"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <flat-pickr
                id="filterMonth"
                v-model="tableProcessing.filter.month"
                class="form-control"
                placeholder="--------- ----"
                :config="flatPickerConfig"
              />
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Memo"
              label-for="filterMemo"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableProcessing.selected.memo"
                placeholder="filter memo here"
                input-id="filterMemo"
                label="memo_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(tableProcessing.options.memos)"
                :loading="(tableProcessing.fetching.memos)"
                :disabled="(tableProcessing.busy || tableProcessing.fetching.memos)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterMemos"
              >
                <template #selected-option="{ memo_name }">
                  <div
                    class="text-break text-wrap text-truncate"
                    style="font-size: 13px;"
                    v-text="memo_name"
                  />
                </template>

                <template #option="{ memo_name, team, creator }">
                  <div class="pb-1">
                    <div
                      class="text-break text-wrap"
                      style="font-size: 13px;"
                      v-text="memo_name"
                    />
                    <div
                      v-if="team?.id && creator?.id"
                      style="font-size: 10px;"
                      class="font-weight-bolder"
                    >
                      {{ getFullName(null, null, creator) }} - {{ team.team_name }}
                    </div>
                  </div>
                </template>

                <template #no-options="">
                  no available memo
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Customer"
              label-for="filterCustomer"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableProcessing.selected.customer"
                placeholder="filter customer here"
                input-id="filterCustomer"
                label="customer_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(tableProcessing.options.customers)"
                :loading="(tableProcessing.fetching.customers)"
                :disabled="(tableProcessing.busy || tableProcessing.fetching.customers)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterCustomers"
              >
                <template #selected-option="{ customer_name }">
                  <div
                    class="text-break text-wrap text-truncate"
                    style="font-size: 13px;"
                    v-text="customer_name"
                  />
                </template>

                <template #option="{ customer_name }">
                  <div
                    class="text-break text-wrap"
                    style="font-size: 13px;"
                    v-text="customer_name"
                  />
                </template>

                <template #no-options="">
                  no available customer
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>

        </BRow>
      </BContainer>
    </BSidebar>

    <BRow>
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BInputGroup>
            <BFormInput
              id="filterSearch"
              v-model="tableProcessing.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
            <BInputGroupAppend>
              <BButton
                v-b-toggle.sbASMCMemoProcessingFilters
                variant="primary"
              >
                <i
                  v-if="[settingFilters].some(isTrue => isTrue)"
                  class="fad fa-spinner fa-spin p-0"
                />
                <i
                  v-else
                  class="fad fa-filter-list"
                /> Filters
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableProcessing"
      :provider="tableProvider"
      :fields="tableProcessing.fields"
      :filter="tableProcessing.filter"
      :filter-on="tableProcessing.filterOn"
      :sort-by="tableProcessing.sortBy"
      :sort-desc="tableProcessing.sortDesc"
      :sort-direction="tableProcessing.sortDirection"
      :total-rows="tableProcessing.totalRows"
      :current-page="tableProcessing.currentPage"
      :per-page="tableProcessing.perPage"
      :stacked="tableProcessing.stacked"
      :busy="tableProcessing.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            @click.prevent="() => $emit('verify:row', item)"
          >
            Verify
          </BButton>
        </div>
      </template>

      <template #cell(assignee)="{ item }">
        <div class="d-flex flex-column gap-1 p-1">
          <div class="font-weight-bolder">
            {{ getFullName(null, null, item.assignee) }}
          </div>
          <div style="font-size: 10px;">
            {{ Array.from(item.assignee.clusters, cluster => cluster.cluster_name).join(', ') || 'n/a' }}
          </div>
        </div>
      </template>

      <template #cell(memo.memo_name)="{ value }">
        <div class="d-flex flex-column gap-1 p-1 text-break">
          {{ value }}
        </div>
      </template>

      <template #cell(customer)="{ item }">
        <div class="d-flex flex-column gap-1 p-1">
          <div class="font-weight-bolder">
            {{ item.customer.customer_name }}
          </div>
          <div style="font-size: 10px;">
            {{ item.customer.address || 'n/a' }}
          </div>
        </div>
      </template>

      <template #cell(is_signed)="{ item, value }">
        <div :class="`font-weight-bold text-${Number(item.is_signed) === 1 ? 'primary' : 'danger'}`">
          {{ value }}
        </div>
      </template>
    </CustomTable>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MCustomers from '@/mixins/accounts/customers'
import MMemos from '@/mixins/maintenances/memos'

import { SASMFsoCustomerMemo } from '@/services/asm'

// import { find } from 'lodash'

export default {
  name: 'ASMCustomerMemosProcessing',

  mixins: [
    MxMisc,
    MxFormatter,
    MCustomers,
    MMemos
  ],

  emits: [
    'create:row',
    'edit:row',
    'verify:row',
    'view:row'
  ],

  data () {
    return {
      api: {},
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false
      },
      tableProcessing: {
        busy: false,
        fetching: {
          memos: false,
          customers: false
        },
        options: {
          memos: [],
          customers: []
        },
        filter: {
          search: '',
          memo: '',
          customer: '',
          month: moment().format('YYYY-MM')
        },
        selected: {
          memo: '',
          customer: '',
          team: {
            id: ''
          }
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          { mobile: 8, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'schedule', label: 'Schedule', class: 'min-w-md-25px text-md-center', formatter: this.monthLongFormatter, sortable: true },
          { mobile: 2, key: 'event_date', class: 'min-w-md-25px text-md-center', formatter: this.dateShortFormatter, sortable: true },
          { mobile: 2, key: 'memo.memo_name', label: 'Memo', class: 'min-w-md-350px', sortable: true },
          { mobile: 3, key: 'assignee', label: 'Technical Officer', class: 'min-w-md-225px', sortable: true },
          { mobile: 4, key: 'customer', label: 'Customer', class: 'min-w-md-350px', sortable: true },
          { mobile: 5, key: 'is_signed', label: 'Signed', class: 'text-md-center min-w-md-50px', formatter: this.yesOrNo },
          { mobile: 6, key: 'created_at', label: 'Assigned At', class: 'min-w-125px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 7, key: 'updated_at', class: 'min-w-125px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    settingFilters () {
      return this.tableProcessing.fetching.customers || this.tableProcessing.fetching.memos
    }
  },

  watch: {
    'tableProcessing.selected.memo' (memo) {
      this.tableProcessing.filter.memo = memo?.id || ''
    },

    'tableProcessing.selected.customer' (customer) {
      this.tableProcessing.filter.customer = customer?.id || ''
    }
  },

  mounted () {
    this.api = this.$refs.tableProcessing

    if (this.currentUser) {
      this.getMemos(this.tableProcessing).finally(() => {
        this.getCustomers(this.tableProcessing)
      })
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableProcessing.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        schedule: filter.month,
        memo: filter.memo,
        customer: filter.customer,
        status: 'Processing'
      }

      return SASMFsoCustomerMemo.get(payload).then(
        ({ data }) => {
          this.tableProcessing.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableProcessing.totalRows = 0
        return []
      }).finally(() => {
        this.tableProcessing.busy = false
      })
    },

    searchFilterMemos (search, loading) {
      this.searchMemosProvider(this.tableProcessing, search, loading)
    },

    searchFilterCustomers (search, loading) {
      this.searchCustomersProvider(this.tableProcessing, search, loading)
    }

  }
}
</script>
